/* Skin analysis page, /pages/skin-analysis (live since 4.10.2026).
   Questions → optional photo → concerns → routine. The page owns no product
   knowledge: GET /api/skin/config and POST /api/skin/routine on the hub read
   Lilach's knowledge blocks; the photo goes to /api/lilach/analyze-skin (the
   same scan as the chat, never stored). Every word comes from #sa-copy (the
   section's settings); on the preview theme an empty setting shows its own
   name as a dashed slot, on the live site it shows nothing. */
(function () {
  "use strict";
  var root = document.getElementById("sa-root");
  if (!root) return;
  // the section sets data-preview on every theme but the published one
  var PREVIEW = root.hasAttribute("data-preview");

  var HUB = (root.getAttribute("data-hub") || "").replace(/\/$/, "");
  var KEY = root.getAttribute("data-key") || "";
  var COPY = JSON.parse(document.getElementById("sa-copy").textContent);
  var LABELS = COPY._labels || {};
  var ICONS = {};
  try {
    ICONS = JSON.parse(document.getElementById("sa-icons").textContent);
  } catch (e) {}
  var stage = root.querySelector("[data-stage]");
  var prog = root.querySelector(".sa-progress");
  var bar = root.querySelector(".sa-progress__bar");
  var MAX_CONCERNS = 2; // the engine's limit (Lilach iron rule 8)
  var MP = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21";
  var FACE_MODEL =
    "https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite";
  var MESH_MODEL =
    "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task";
  var reduceMotion =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  var FLOW = [
    "feel",
    "shine",
    "cream",
    "breakouts",
    "photo",
    "concerns",
    "life",
    "size",
  ];
  var Q = {
    feel: {
      q: "q_feel",
      opts: [
        ["tight", "o_feel_tight", "dry"],
        ["combo", "o_feel_combo", "combo"],
        ["oily", "o_feel_oily", "oily"],
        ["itchy", "o_feel_itchy", "red"],
      ],
    },
    shine: {
      q: "q_shine",
      opts: [
        ["all", "o_shine_all", "oily"],
        ["tzone", "o_shine_tzone", "combo"],
        ["none", "o_shine_none", "dry"],
        ["red", "o_shine_red", "red"],
      ],
    },
    cream: {
      q: "q_cream",
      opts: [
        ["light", "o_cream_light", null, "o_cream_light_sub"],
        ["rich", "o_cream_rich", null],
      ],
    },
    breakouts: {
      q: "q_breakouts",
      opts: [
        ["none", "o_br_none"],
        ["few", "o_br_few"],
        ["chronic", "o_br_chronic"],
        ["cystic", "o_br_cystic"],
      ],
    },
    life: {
      q: "q_life",
      opts: [
        ["none", "o_life_none"],
        ["pregnant", "o_life_pregnant"],
        ["nursing", "o_life_nursing"],
      ],
    },
    size: {
      q: "q_size",
      opts: [
        ["minimal", "o_size_minimal"],
        ["full", "o_size_full"],
      ],
    },
  };

  var state = {
    step: "intro",
    a: { concerns: [] },
    scan: null,
    fromScan: [],
    cfg: null,
    result: null,
    time: "am",
    consent: false,
    photo: null,
  };

  // ── tiny DOM builder: text always goes in as text, never as HTML ──
  function h(tag, attrs, kids) {
    var el = document.createElement(tag);
    if (attrs)
      for (var k in attrs) {
        var v = attrs[k];
        if (v == null || v === false) continue;
        if (k === "class") el.className = v;
        else if (k.slice(0, 2) === "on") el.addEventListener(k.slice(2), v);
        else el.setAttribute(k, v === true ? "" : v);
      }
    (Array.isArray(kids) ? kids : kids == null ? [] : [kids]).forEach(
      function (c) {
        if (c == null || c === false) return;
        el.appendChild(
          typeof c === "string" || typeof c === "number"
            ? document.createTextNode(String(c))
            : c,
        );
      },
    );
    return el;
  }
  function t(key, tokens) {
    var s = COPY[key];
    if (!s)
      return PREVIEW
        ? h("span", { class: "sa-slot" }, "[" + (LABELS[key] || key) + "]")
        : document.createTextNode("");
    if (tokens)
      for (var k in tokens) s = s.split("{" + k + "}").join(String(tokens[k]));
    return document.createTextNode(s);
  }
  function str(key) {
    return COPY[key] || "";
  }
  function money(n) {
    return "₪" + Math.round(n).toLocaleString("he-IL");
  }

  function api(path, opts) {
    opts = opts || {};
    opts.headers = Object.assign({ "x-widget-key": KEY }, opts.headers || {});
    return fetch(HUB + path, opts).then(function (r) {
      return r
        .json()
        .catch(function () {
          return {};
        })
        .then(function (j) {
          if (!r.ok || j.ok === false) throw j;
          return j;
        });
    });
  }
  var cfgPromise = api("/api/skin/config").then(function (c) {
    state.cfg = c;
    return c;
  });

  function setProgress() {
    var i = FLOW.indexOf(state.step);
    if (i < 0) {
      prog.hidden = true;
      return;
    }
    prog.hidden = false;
    var pct = Math.round(((i + 1) / FLOW.length) * 100);
    bar.style.width = pct + "%";
    prog.setAttribute("aria-valuenow", String(pct));
  }

  function show(node) {
    stopCamera();
    stage.innerHTML = "";
    node.classList.add("sa-step");
    if (state.step !== "results") node.classList.add("sa-step--fill");
    root.style.setProperty("--sa-top", Math.round(stickyBottom() + 24) + "px");
    root.style.setProperty("--sa-bottom", Math.round(fixedFloor()) + "px");
    stage.appendChild(node);
    setProgress();
    root.classList.toggle("sa--wide", state.step === "results");
    var head = node.querySelector("h2");
    if (head) {
      head.setAttribute("tabindex", "-1");
      try {
        head.focus({ preventScroll: true });
      } catch (e) {}
    }
    // Bring the new step's top into view below the site's fixed bars.
    var cover = stickyBottom();
    var rect = root.getBoundingClientRect();
    if (rect.top < cover || rect.top > window.innerHeight * 0.6)
      window.scrollTo({
        top: rect.top + window.scrollY - cover - 12,
        behavior: reduceMotion ? "auto" : "smooth",
      });
  }

  // How far down the site's fixed/sticky bars (promo strip, header) reach.
  function stickyBottom() {
    var y = 2,
      bottom = 0;
    for (var n = 0; n < 4; n++) {
      var el = document.elementFromPoint(window.innerWidth / 2, y);
      var fixed = null;
      while (el && el !== document.body) {
        var pos = getComputedStyle(el).position;
        if (pos === "fixed" || pos === "sticky") fixed = el;
        el = el.parentElement;
      }
      if (!fixed || root.contains(fixed)) break;
      var b = fixed.getBoundingClientRect().bottom;
      if (b <= bottom || b > window.innerHeight / 2) break;
      bottom = b;
      y = b + 2;
    }
    return bottom;
  }

  // How far up a full-width fixed bar at the bottom reaches (Shopify's preview
  // bar, a site bottom bar), so a step's buttons stay above it.
  function fixedFloor() {
    var el = document.elementFromPoint(
      window.innerWidth / 2,
      window.innerHeight - 2,
    );
    while (el && el !== document.body) {
      if (getComputedStyle(el).position === "fixed" && !root.contains(el)) {
        var r = el.getBoundingClientRect();
        var h = window.innerHeight - r.top;
        return r.width >= window.innerWidth * 0.6 && h < window.innerHeight / 3
          ? h
          : 0;
      }
      el = el.parentElement;
    }
    return 0;
  }

  // The model's own words reach her too: no dash in customer Hebrew (same rule as the engine's noDash).
  function noDash(x) {
    return String(x || "")
      .replace(/(\d)\s*[-‐-—־]\s*(\d)/g, "$1 עד $2")
      .replace(/([֐-׿])[-‐-—־]([֐-׿])/g, "$1 $2")
      .replace(/\s*[-‐-—־]+\s*/g, ", ")
      .replace(/^,\s*|,\s*$/g, "")
      .replace(/,\s*([.!?)])/g, "$1");
  }

  function go(step) {
    state.step = step;
    render();
  }
  function next() {
    var i = FLOW.indexOf(state.step);
    if (i < 0) return go(FLOW[0]);
    if (i + 1 >= FLOW.length) return results();
    go(FLOW[i + 1]);
  }
  function back() {
    var i = FLOW.indexOf(state.step);
    if (i <= 0) return go("intro");
    go(FLOW[i - 1]);
  }

  function count() {
    return null;
  }
  function navRow(withNext, nextDisabled) {
    return h("div", { class: "sa-nav" }, [
      withNext
        ? h(
            "button",
            {
              type: "button",
              class: "sa-btn",
              disabled: nextDisabled || null,
              onclick: next,
              "data-next": "1",
            },
            t("next"),
          )
        : h("span"),
      h(
        "button",
        { type: "button", class: "sa-back", onclick: back },
        t("back"),
      ),
    ]);
  }

  function icon(kind) {
    if (kind === "red") {
      var sv = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      sv.setAttribute("class", "sa-opt__icon");
      sv.setAttribute("viewBox", "0 0 44 44");
      sv.setAttribute("aria-hidden", "true");
      sv.innerHTML =
        '<circle cx="22" cy="22" r="20" fill="#fff" stroke="#6f6f6f" stroke-width="1.6"/>' +
        '<path d="M12 25c2-2 4-2 6 0s4 2 6 0 4-2 6 0 4 2 4 1" fill="none" stroke="#6f6f6f" stroke-width="1.6" stroke-linecap="round"/>' +
        '<path d="M15 17l2-3M22 15v-4M29 17l-2-3" fill="none" stroke="#6f6f6f" stroke-width="1.6" stroke-linecap="round"/>';
      return sv;
    }
    if (kind && ICONS[kind])
      return h("img", {
        class: "sa-opt__icon",
        src: ICONS[kind],
        alt: "",
        width: 44,
        height: 44,
        loading: "lazy",
      });
    return null;
  }

  // ── a single-choice question: picking an answer moves on by itself ──
  function single(name) {
    var def = Q[name];
    var list = h("div", {
      class: "sa-opts",
      role: "radiogroup",
      "aria-labelledby": "sa-q-" + name,
    });
    def.opts.forEach(function (o) {
      var on = state.a[name] === o[0];
      var btn = h(
        "button",
        {
          type: "button",
          class: "sa-opt",
          role: "radio",
          "aria-checked": on ? "true" : "false",
          onclick: function () {
            state.a[name] = o[0];
            list.querySelectorAll(".sa-opt").forEach(function (b) {
              b.setAttribute("aria-checked", b === btn ? "true" : "false");
            });
            setTimeout(next, reduceMotion ? 0 : 260);
          },
        },
        [
          icon(o[2]),
          h("span", { class: "sa-opt__text" }, [
            t(o[1]),
            o[3] ? h("span", { class: "sa-opt__sub" }, t(o[3])) : null,
          ]),
          h("span", { class: "sa-opt__check", "aria-hidden": "true" }),
        ],
      );
      list.appendChild(btn);
    });
    return h("div", null, [
      count(),
      h("h2", { id: "sa-q-" + name }, t(def.q)),
      list,
      navRow(false),
    ]);
  }

  // ── the concerns: up to MAX, what the photo saw comes pre-selected ──
  function concerns() {
    var wrap = h("div");
    var cfg = state.cfg;
    if (!cfg) {
      wrap.appendChild(
        h("div", { class: "sa-spinner", role: "status", "aria-label": "…" }),
      );
      cfgPromise.then(render, function () {
        wrap.innerHTML = "";
        wrap.appendChild(h("p", { class: "sa-error" }, "⚠"));
      });
      return wrap;
    }
    var list = h("div", {
      class: "sa-opts sa-opts--chips",
      role: "group",
      "aria-labelledby": "sa-q-concerns",
    });
    function paint() {
      var full = state.a.concerns.length >= MAX_CONCERNS;
      list.querySelectorAll(".sa-chip").forEach(function (c) {
        var on = state.a.concerns.indexOf(c.getAttribute("data-label")) >= 0;
        c.setAttribute("aria-checked", on ? "true" : "false");
        c.setAttribute("aria-disabled", !on && full ? "true" : "false");
      });
    }
    cfg.concerns.forEach(function (label) {
      var fromScan = state.fromScan.indexOf(label) >= 0;
      list.appendChild(
        h(
          "button",
          {
            type: "button",
            class: "sa-chip",
            role: "checkbox",
            "data-label": label,
            onclick: function () {
              var i = state.a.concerns.indexOf(label);
              if (i >= 0) state.a.concerns.splice(i, 1);
              else if (state.a.concerns.length < MAX_CONCERNS)
                state.a.concerns.push(label);
              paint();
            },
          },
          [fromScan ? camIcon() : null, label],
        ),
      );
    });
    paint();
    wrap.appendChild(h("h2", { id: "sa-q-concerns" }, t("q_concerns")));
    wrap.appendChild(
      h("p", { class: "sa-hint" }, t("concerns_hint", { max: MAX_CONCERNS })),
    );
    wrap.appendChild(list);
    wrap.appendChild(navRow(true));
    return wrap;
  }
  function camIcon() {
    var s = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    s.setAttribute("class", "sa-chip__cam");
    s.setAttribute("viewBox", "0 0 24 24");
    s.setAttribute("aria-hidden", "true");
    s.innerHTML =
      '<path fill="currentColor" d="M9 4 7.2 6H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-3.2L15 4H9Zm3 4.5a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Zm0 2a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Z"/>';
    return s;
  }

  // ── the photo: consent, a live camera that waits for a good frame, the scan ──
  var cam = { stream: null, raf: 0, detector: null, video: null, okSince: 0 };
  if (PREVIEW) root._saCam = cam; // preview QA reads the camera gate
  function stopCamera() {
    if (cam.raf) cancelAnimationFrame(cam.raf);
    cam.raf = 0;
    if (cam.stream)
      cam.stream.getTracks().forEach(function (tr) {
        tr.stop();
      });
    cam.stream = null;
  }

  function photo() {
    var cfg = state.cfg;
    var wrap = h("div");
    wrap.appendChild(h("h2", null, t("photo_title")));
    wrap.appendChild(h("p", { class: "sa-lead" }, t("photo_sub")));
    if (state.scan) {
      wrap.appendChild(scanSummary(state.scan));
      wrap.appendChild(
        h("div", { class: "sa-nav" }, [
          h(
            "button",
            { type: "button", class: "sa-btn", onclick: next },
            t("next"),
          ),
          h(
            "button",
            {
              type: "button",
              class: "sa-link",
              onclick: function () {
                state.scan = null;
                state.fromScan = [];
                go("photo");
              },
            },
            t("photo_retake"),
          ),
        ]),
      );
      return wrap;
    }
    if (!cfg) {
      cfgPromise.then(render, function () {});
      wrap.appendChild(h("div", { class: "sa-spinner" }));
      return wrap;
    }
    var c = cfg.consent;
    var box = h("div", { class: "sa-consent" }, [
      h("p", null, c.intro),
      h("p", { style: "margin-top:10px;font-weight:500" }, c.privacyTitle),
      h(
        "ul",
        null,
        c.privacy.map(function (l) {
          return h("li", null, [svgIcon("check"), h("span", null, l)]);
        }),
      ),
      h("p", { class: "sa-consent__legal" }, c.legal),
    ]);
    var cb = h("input", { type: "checkbox", id: "sa-consent" });
    cb.checked = state.consent;
    var openBtn = h("button", {
      type: "button",
      class: "sa-btn sa-btn--block",
      onclick: openCamera,
    });
    function openLabel() {
      openBtn.innerHTML = "";
      if (state.consent) openBtn.appendChild(svgIcon("camera"));
      openBtn.appendChild(
        t(state.consent ? "photo_open" : "photo_need_consent"),
      );
    }
    openLabel();
    openBtn.disabled = !state.consent;
    cb.addEventListener("change", function () {
      state.consent = cb.checked;
      // She means to take a photo: start fetching the face model now.
      if (cb.checked) loadDetector();
      openBtn.disabled = !cb.checked;
      openLabel();
    });
    box.appendChild(
      h("label", { class: "sa-check", for: "sa-consent" }, [
        cb,
        h("span", null, c.checkbox),
      ]),
    );
    wrap.appendChild(box);
    wrap.appendChild(h("p", { class: "sa-hint" }, t("photo_tip")));
    var file = h("input", { type: "file", accept: "image/*", hidden: true });
    file.addEventListener("change", function () {
      var f = file.files && file.files[0];
      if (f) fileToJpeg(f).then(preview);
    });
    wrap.appendChild(h("div", { class: "sa-row" }, [openBtn]));
    wrap.appendChild(
      h("div", { class: "sa-nav" }, [
        h(
          "button",
          {
            type: "button",
            class: "sa-link",
            "data-upload": "1",
            onclick: function () {
              if (state.consent) file.click();
              else cb.focus();
            },
          },
          t("photo_upload"),
        ),
        h(
          "button",
          { type: "button", class: "sa-link", onclick: next },
          t("skip"),
        ),
      ]),
    );
    wrap.appendChild(file);
    return wrap;
  }

  function openCamera() {
    if (!state.consent) return;
    cam.seen = cam.many = false; // a face seen at any moment of this camera session
    var wrap = h("div");
    wrap.appendChild(h("h2", null, t("photo_title")));
    var video = h("video", { playsinline: true, muted: true, autoplay: true });
    video.muted = true;
    var box = h("div", { class: "sa-cam" }, [video]);
    var svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 300 400");
    svg.setAttribute("preserveAspectRatio", "xMidYMid slice");
    svg.innerHTML =
      '<ellipse class="sa-oval" cx="150" cy="190" rx="98" ry="132"/>';
    box.appendChild(svg);
    var chipLight = h("span", { class: "sa-status" }, t("chip_light"));
    var chipStraight = h("span", { class: "sa-status" }, t("chip_straight"));
    var shutter = h("button", {
      type: "button",
      class: "sa-shutter",
      "aria-label": str("photo_open"),
      disabled: true,
    });
    wrap.appendChild(box);
    wrap.appendChild(
      h("div", { class: "sa-chips", "aria-live": "polite" }, [
        chipStraight,
        chipLight,
      ]),
    );
    wrap.appendChild(shutter);
    wrap.appendChild(
      h("div", { class: "sa-nav" }, [
        h("span"),
        h(
          "button",
          {
            type: "button",
            class: "sa-link",
            onclick: function () {
              go("photo");
            },
          },
          t("back"),
        ),
      ]),
    );
    show(wrap);
    cam.video = video;
    shutter.addEventListener("click", function () {
      capture();
    });

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia)
      return fallbackToUpload();
    navigator.mediaDevices
      .getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 1280 },
          height: { ideal: 1706 },
        },
        audio: false,
      })
      .then(function (stream) {
        cam.stream = stream;
        video.srcObject = stream;
        return video.play();
      })
      .then(function () {
        loadDetector();
        var started = Date.now();
        var sample = h("canvas", { width: 48, height: 64 });
        var sctx = sample.getContext("2d", { willReadFrequently: true });
        var last = 0;
        function tick(ts) {
          cam.raf = requestAnimationFrame(tick);
          if (ts - last < 150 || video.readyState < 2) return;
          last = ts;
          var face = cam.detector ? faceCheck(video, ts) : null;
          cam.last = face;
          var lt = lighting(video, sctx, face && face.box);
          var lightOk = lt.ok;
          cam.lightMean = lt.mean;
          var dark = lt.mean <= 0.25;
          if (dark !== cam.dark) {
            cam.dark = dark;
            chipLight.textContent =
              (dark && str("hint_dark")) || str("chip_light");
          }
          var straightOk = face ? face.ok : false;
          chipLight.classList.toggle("is-ok", lightOk);
          chipStraight.classList.toggle("is-ok", straightOk);
          // The photo takes itself only when the face model sees one level,
          // centred face in even light. The shutter is always there after 8s,
          // so a slow phone or a dim room never leaves her stuck.
          var ready = lightOk && straightOk;
          box.classList.toggle("is-ready", ready);
          shutter.disabled = !(ready || Date.now() - started > 8000);
          if (ready) {
            if (!cam.okSince) cam.okSince = ts;
            if (ts - cam.okSince > 900) capture();
          } else cam.okSince = 0;
        }
        cam.raf = requestAnimationFrame(tick);
      })
      .catch(fallbackToUpload);

    function fallbackToUpload() {
      go("photo");
      var up = stage.querySelector("[data-upload]");
      if (up) up.click();
    }
  }

  // Shopify minifies theme JS and turns import() into require(); a module
  // script built at runtime is left alone.
  function loadVision() {
    return new Promise(function (resolve, reject) {
      if (window.__saVision) return resolve(window.__saVision);
      var done = function () {
        window.removeEventListener("sa:vision", done);
        if (window.__saVision) resolve(window.__saVision);
        else reject(new Error("vision bundle failed"));
      };
      window.addEventListener("sa:vision", done);
      var sc = document.createElement("script");
      sc.type = "module";
      sc.textContent =
        'import * as v from "' +
        MP +
        '/vision_bundle.mjs";' +
        "window.__saVision = v;" +
        'window.dispatchEvent(new Event("sa:vision"));';
      sc.onerror = done;
      document.head.appendChild(sc);
      setTimeout(done, 20000);
    });
  }

  function loadDetector() {
    if (cam.detector || cam.loading) return;
    cam.loading = true;
    loadVision()
      .then(function (vision) {
        return vision.FilesetResolver.forVisionTasks(MP + "/wasm").then(
          function (fileset) {
            var make = function (delegate) {
              return vision.FaceDetector.createFromOptions(fileset, {
                baseOptions: { modelAssetPath: FACE_MODEL, delegate: delegate },
                runningMode: "VIDEO",
                minDetectionConfidence: 0.6,
              });
            };
            // CPU first: the tiny face model runs in a few ms on any phone,
            // while WebGL can stall (software GL, older GPUs).
            return make("CPU").catch(function () {
              return make("GPU");
            });
          },
        );
      })
      .then(function (d) {
        cam.detector = d;
      })
      .catch(function (e) {
        cam.loading = false;
        cam.err = String((e && (e.stack || e.message)) || e).slice(0, 900);
      });
  }

  // Light inside the oval: bright enough, not blown out, the two cheeks even.
  // Light on the face: bright enough, not blown out, the two cheeks within
  // 0.2 of each other (the published capture gates use the same number).
  function lighting(video, ctx, fb) {
    var w = 48,
      hh = 64;
    var vw = video.videoWidth,
      vh = video.videoHeight;
    // the face box when the model found it, else the middle of the oval
    var b = fb || { x: 0.3, y: 0.32, w: 0.4, h: 0.4 };
    ctx.drawImage(video, b.x * vw, b.y * vh, b.w * vw, b.h * vh, 0, 0, w, hh);
    var d = ctx.getImageData(0, 0, w, hh).data;
    var left = 0,
      right = 0,
      nl = 0,
      nr = 0;
    // cheeks: the lower middle of the face, away from hair and eyes
    for (var y = Math.round(hh * 0.45); y < Math.round(hh * 0.78); y++)
      for (var x = Math.round(w * 0.12); x < Math.round(w * 0.88); x++) {
        if (x > w * 0.4 && x < w * 0.6) continue; // the nose
        var i = (y * w + x) * 4;
        var l = (0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2]) / 255;
        if (x < w / 2) {
          left += l;
          nl++;
        } else {
          right += l;
          nr++;
        }
      }
    left /= nl || 1;
    right /= nr || 1;
    var mean = (left + right) / 2;
    return {
      ok: mean > 0.25 && mean < 0.88 && Math.abs(left - right) <= 0.2,
      mean: mean,
    };
  }

  // One face, big enough, centred, level and facing the camera.
  function faceCheck(video, ts) {
    var r;
    try {
      r = cam.detector.detectForVideo(video, ts);
    } catch (e) {
      return null;
    }
    var dets = (r && r.detections) || [];
    if (dets.length > 1) cam.many = true;
    if (dets.length !== 1) return { ok: false };
    cam.seen = true;
    var bb = dets[0].boundingBox;
    var vw = video.videoWidth,
      vh = video.videoHeight;
    var wRatio = bb.width / vw;
    var cx = (bb.originX + bb.width / 2) / vw - 0.5;
    var cy = (bb.originY + bb.height / 2) / vh - 0.47;
    var kp = dets[0].keypoints || [];
    var tilted = false,
      turned = false;
    if (kp.length >= 3) {
      var re = kp[0],
        le = kp[1],
        nose = kp[2];
      var dx = (le.x - re.x) * vw,
        dy = (le.y - re.y) * vh;
      var roll = Math.abs((Math.atan2(dy, dx) * 180) / Math.PI);
      if (roll > 90) roll = 180 - roll;
      tilted = roll > 10;
      var mid = (re.x + le.x) / 2;
      var eyeDist = Math.abs(le.x - re.x) || 0.001;
      turned = Math.abs(nose.x - mid) / eyeDist > 0.28;
    }
    return {
      m: { wRatio: wRatio, cx: cx, cy: cy, tilted: tilted, turned: turned },
      box: {
        x: Math.max(0, bb.originX / vw),
        y: Math.max(0, bb.originY / vh),
        w: Math.min(1, bb.width / vw),
        h: Math.min(1, bb.height / vh),
      },
      // normalized: right eye, left eye, nose tip, mouth (then the ears)
      kp: kp.map(function (p) {
        return { x: p.x, y: p.y };
      }),
      ok:
        wRatio > 0.42 &&
        wRatio < 0.9 &&
        Math.abs(cx) < 0.14 &&
        Math.abs(cy) < 0.18 &&
        !tilted &&
        !turned,
    };
  }

  // The scan always sees the face at one scale: the frame is cropped to the face
  // (3:4, forehead to chin) before it is sent. Measured 4.10: the same photo read
  // the same 4 times out of 4, but the same face framed close read dryness 2 and
  // framed far read 1. No face box = the whole frame, as before.
  function faceCrop(face, W, H, k) {
    if (!face || !face.box || !face.kp || face.kp.length < 4) return null;
    var b = face.box;
    var cw = Math.min(W, b.w * W * (k || 1.5));
    var ch = Math.min(H, cw / 0.75);
    var sx = Math.max(0, Math.min(W - cw, (b.x + b.w / 2) * W - cw / 2));
    var sy = Math.max(0, Math.min(H - ch, (b.y + b.h * 0.42) * H - ch / 2));
    return {
      sx: sx,
      sy: sy,
      sw: cw,
      sh: ch,
      kp: face.kp.map(function (p) {
        return { x: (p.x * W - sx) / cw, y: (p.y * H - sy) / ch };
      }),
    };
  }

  // Draws what gets analyzed. The main crop (no k) keeps the face points for the
  // map on the result; a k crop is one of the two extra readings.
  function drawFace(source, W, H, face, k) {
    var cr = faceCrop(face, W, H, k) || {
      sx: 0,
      sy: 0,
      sw: W,
      sh: H,
      kp: null,
    };
    // a face crop always goes out 768 wide, so the scan reads every face at one size
    var s = cr.kp ? 768 / cr.sw : Math.min(1, 1024 / Math.max(cr.sw, cr.sh));
    var c = h("canvas", {
      width: Math.round(cr.sw * s),
      height: Math.round(cr.sh * s),
    });
    c.getContext("2d").drawImage(
      source,
      cr.sx,
      cr.sy,
      cr.sw,
      cr.sh,
      0,
      0,
      c.width,
      c.height,
    );
    if (!k) {
      state.faceKp = cr.kp;
      state.faceAspect = c.width / c.height;
    }
    return c;
  }

  // The wider and the tighter crop, drawn while the frame is still there.
  function extraCrops(source, W, H, face) {
    if (!face || !face.kp) return Promise.resolve([]);
    return Promise.all(
      [1.4, 1.62].map(function (k) {
        var c = drawFace(source, W, H, face, k);
        return new Promise(function (res) {
          c.toBlob(res, "image/jpeg", 0.9);
        });
      }),
    ).then(function (bs) {
      return bs.filter(Boolean);
    });
  }

  // In a dim room the screen turns white for a moment and lights her face, like a
  // phone's front flash (Haut.AI's screen flashlight, research 4.10.2026).
  function capture() {
    var video = cam.video;
    if (!video || !video.videoWidth || cam.flash) return;
    if (!(cam.lightMean < 0.35)) return take(video);
    cam.flash = h("div", { class: "sa-flash", "aria-hidden": "true" });
    document.body.appendChild(cam.flash);
    setTimeout(function () {
      if (cam.stream) take(video);
      cam.flash.remove();
      cam.flash = null;
    }, 800);
  }

  function take(video) {
    // one face at any moment of the session is enough; -1 = the detector never loaded
    state.faceCount = !cam.detector ? -1 : cam.seen ? 1 : cam.many ? 2 : 0;
    var face = cam.last && cam.last.kp ? cam.last : null;
    var c = drawFace(video, video.videoWidth, video.videoHeight, face);
    var extra = extraCrops(video, video.videoWidth, video.videoHeight, face);
    meshFor(c);
    stopCamera();
    c.toBlob(
      function (b) {
        if (b)
          extra.then(function (bs) {
            state.photoCrops = bs;
            preview(b);
          });
      },
      "image/jpeg",
      0.9,
    );
  }

  // A still photo gets its face box from the same model, switched to IMAGE mode.
  function detectStill(source) {
    loadDetector();
    return new Promise(function (resolve) {
      var t0 = Date.now();
      (function wait() {
        if (cam.detector) return resolve(cam.detector);
        if (cam.err || Date.now() - t0 > 8000) return resolve(null);
        setTimeout(wait, 150);
      })();
    }).then(function (d) {
      if (!d) return null;
      var back = function (face) {
        return Promise.resolve(d.setOptions({ runningMode: "VIDEO" })).then(
          function () {
            return face;
          },
          function () {
            return face;
          },
        );
      };
      return Promise.resolve(d.setOptions({ runningMode: "IMAGE" }))
        .then(function () {
          var dets = (d.detect(source) || {}).detections || [];
          state.faceCount = dets.length;
          if (dets.length !== 1) return null;
          var bb = dets[0].boundingBox;
          return {
            box: {
              x: bb.originX / source.width,
              y: bb.originY / source.height,
              w: bb.width / source.width,
              h: bb.height / source.height,
            },
            kp: (dets[0].keypoints || []).map(function (p) {
              return { x: p.x, y: p.y };
            }),
          };
        })
        .catch(function () {
          return null;
        })
        .then(back);
    });
  }

  function fileToJpeg(f) {
    state.faceCount = -1; // set by detectStill when the detector runs
    return new Promise(function (resolve) {
      var img = new Image();
      img.onload = function () {
        var s = Math.min(1, 1280 / Math.max(img.width, img.height));
        var c = h("canvas", {
          width: Math.round(img.width * s),
          height: Math.round(img.height * s),
        });
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        // The face box comes from the small copy, the crop from the photo itself.
        // Measured 4.10.2026: cropping the 1280 copy turned a phone photo taken at
        // arm's length into a 400px face stretched to 768, and the skin texture was gone.
        detectStill(c).then(function (face) {
          var main = drawFace(img, img.width, img.height, face);
          meshFor(main);
          extraCrops(img, img.width, img.height, face).then(function (bs) {
            URL.revokeObjectURL(img.src);
            state.photoCrops = bs;
            main.toBlob(resolve, "image/jpeg", 0.9);
          });
        });
      };
      img.src = URL.createObjectURL(f);
    });
  }

  function preview(blob) {
    state.photo = blob;
    var url = URL.createObjectURL(blob);
    if (state.photoUrl) URL.revokeObjectURL(state.photoUrl);
    state.photoUrl = url;
    var wrap = h("div");
    wrap.appendChild(h("h2", null, t("photo_title")));
    wrap.appendChild(
      h("div", { class: "sa-cam" }, [h("img", { src: url, alt: "" })]),
    );
    var analyzeBtn = h(
      "button",
      {
        type: "button",
        class: "sa-btn",
        onclick: function () {
          analyze(analyzeBtn);
        },
      },
      t("photo_analyze"),
    );
    wrap.appendChild(
      h("div", { class: "sa-row" }, [
        analyzeBtn,
        h(
          "button",
          {
            type: "button",
            class: "sa-btn sa-btn--ghost",
            onclick: function () {
              state.photo = null;
              openCamera();
            },
          },
          t("photo_retake"),
        ),
      ]),
    );
    wrap.appendChild(
      h("div", { class: "sa-nav" }, [
        h("span"),
        h(
          "button",
          { type: "button", class: "sa-link", onclick: next },
          t("skip"),
        ),
      ]),
    );
    show(wrap);
  }

  function skinUid() {
    try {
      var u = localStorage.getItem("asif_skin_uid");
      if (!u) {
        u =
          (crypto.randomUUID && crypto.randomUUID()) ||
          String(Date.now()) + Math.random().toString(16).slice(2);
        localStorage.setItem("asif_skin_uid", u);
      }
      return u;
    } catch (e) {
      return "";
    }
  }

  function analyze(btn) {
    if (!state.photo) return;
    function failed(e) {
      go("photo");
      var msg =
        (e && e.code && str("err_" + e.code)) ||
        (e && (e.message || e.error)) ||
        "⚠";
      // right under the title, in view: at the bottom of the step it sat below the
      // fold on a phone and she saw the consent box again with no reason (4.10.2026)
      var step = stage.querySelector(".sa-step");
      var err = h("p", { class: "sa-error", role: "alert" }, msg);
      var lead = step.querySelector(".sa-lead") || step.querySelector("h2");
      if (lead) lead.after(err);
      else step.appendChild(err);
      err.scrollIntoView({ block: "center" });
    }
    // the page's face detector saw no face, or more than one: nothing is sent
    // (a reading of a neck or of two people cannot be right)
    var local =
      state.faceCount === 0
        ? "no_face"
        : state.faceCount > 1
          ? "several_faces"
          : "";
    if (local) return failed({ code: local });
    btn.classList.add("is-busy");
    var fd = new FormData();
    fd.append("file", state.photo, "skin.jpg");
    (state.photoCrops || []).forEach(function (b, i) {
      fd.append("file_c" + (i + 2), b, "skin" + (i + 2) + ".jpg");
    });
    fd.append(
      "sessionId",
      "sa_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8),
    );
    fd.append("consentGiven", "true");
    var uid = skinUid();
    if (uid) fd.append("skinUid", uid);
    var wrap = h("div", null, [
      h("div", {
        class: "sa-spinner",
        role: "status",
        "aria-label": str("photo_analyze"),
      }),
    ]);
    show(wrap);
    api("/api/lilach/analyze-skin", { method: "POST", body: fd })
      .then(function (j) {
        state.photo = null;
        state.scan = j.analysis || {};
        var map = (state.cfg && state.cfg.scanConcern) || {};
        var scores = state.scan.scores || {};
        // the photo marks a concern only when it is clear about it: 3+, or 2+ in all
        // three readings (scores_min). Research 4.10.2026: a 2 on one crop of a face
        // without redness had marked "sensitive".
        var low = state.scan.scores_min || {};
        state.fromScan = Object.keys(map)
          .filter(function (k) {
            return (scores[k] || 0) >= 3 || (low[k] || 0) >= 2;
          })
          .sort(function (x, y) {
            return (scores[y] || 0) - (scores[x] || 0);
          })
          .map(function (k) {
            return map[k];
          })
          .slice(0, MAX_CONCERNS);
        state.fromScan.forEach(function (l) {
          if (
            state.a.concerns.indexOf(l) < 0 &&
            state.a.concerns.length < MAX_CONCERNS
          )
            state.a.concerns.push(l);
        });
        go("photo");
      })
      .catch(failed);
  }

  // The face mesh (478 points) is read from the photo while the scan runs, so the
  // zones follow her own face: a smile lifts the cheeks, a long forehead stays whole.
  function meshFor(canvas) {
    state.faceMesh = null;
    loadVision()
      .then(function (vision) {
        return (
          cam.landmarker ||
          vision.FilesetResolver.forVisionTasks(MP + "/wasm").then(
            function (fileset) {
              return vision.FaceLandmarker.createFromOptions(fileset, {
                baseOptions: { modelAssetPath: MESH_MODEL, delegate: "CPU" },
                runningMode: "IMAGE",
                numFaces: 1,
              });
            },
          )
        );
      })
      .then(function (lm) {
        cam.landmarker = lm;
        var pts = ((lm.detect(canvas) || {}).faceLandmarks || [])[0];
        if (!pts || pts.length < 468) return;
        state.faceMesh = pts.map(function (p) {
          return { x: p.x, y: p.y };
        });
        var old = state.scan && stage.querySelector(".sa-map");
        if (old) old.replaceWith(faceMap(state.scan, zoneHits(state.scan)));
      })
      .catch(function (e) {
        cam.meshErr = String((e && e.message) || e).slice(0, 200);
      });
  }

  // Each zone: [cx, cy, rx, ry, side, where its number sits: x and y in radii],
  // in percent of the photo. A paired zone lists the photo's left one first.
  function meshShapes(mesh) {
    var box = function (ids, side, kx, ky) {
      var xs = ids.map(function (i) {
          return mesh[i].x * 100;
        }),
        ys = ids.map(function (i) {
          return mesh[i].y * 100;
        });
      var x0 = Math.min.apply(null, xs),
        x1 = Math.max.apply(null, xs),
        y0 = Math.min.apply(null, ys),
        y1 = Math.max.apply(null, ys);
      return [
        (x0 + x1) / 2,
        (y0 + y1) / 2,
        ((x1 - x0) / 2) * 1.08,
        ((y1 - y0) / 2) * 1.12,
        side,
        kx,
        ky,
      ];
    };
    // MediaPipe face mesh points; her right side is the photo's left
    return {
      forehead: [
        box(
          [
            10, 109, 67, 103, 338, 297, 332, 151, 9, 107, 66, 105, 336, 296,
            334,
          ],
          1,
          0.8,
          -0.7,
        ),
      ],
      nose: [
        box([168, 6, 197, 195, 5, 4, 1, 2, 98, 327, 64, 294], 1, 1.1, -0.6),
      ],
      cheeks: [
        box(
          [117, 118, 101, 50, 205, 187, 123, 147, 36, 207, 206],
          -1,
          0.85,
          0.7,
        ),
        box(
          [346, 347, 330, 280, 425, 411, 352, 376, 266, 427, 426],
          1,
          0.85,
          0.7,
        ),
      ],
      under_eyes: [
        box([31, 228, 229, 230, 231, 232, 233, 119, 120, 121], -1, 1.3, 0),
        box([261, 448, 449, 450, 451, 452, 453, 348, 349, 350], 1, 1.3, 0),
      ],
      chin: [
        box(
          [152, 148, 176, 149, 377, 400, 378, 175, 199, 200, 18, 83, 313],
          1,
          0.8,
          -0.7,
        ),
      ],
    };
  }

  // Without the mesh: the same zones from the detector's six points.
  function pointShapes(kp) {
    var A = state.faceAspect || 0.75; // width / height of the photo
    var re = kp[0],
      le = kp[1],
      nose = kp[2],
      mouth = kp[3];
    var X = function (v) {
      return v * 100;
    };
    var d = Math.abs(le.x - re.x) * 100; // eye distance, in width units
    var v = function (k) {
      return k * d * A; // a distance of k eye-distances, in height units
    };
    var midX = X((re.x + le.x) / 2),
      midY = X((re.y + le.y) / 2);
    var eyes = [re, le]
      .map(function (e) {
        return { x: X(e.x), y: X(e.y), out: X(e.x) < midX ? -1 : 1 };
      })
      .sort(function (a, b) {
        return a.x - b.x;
      });
    return {
      forehead: [[midX, midY - v(0.9), 0.95 * d, v(0.42), 1, 0.8, -0.7]],
      nose: [
        [
          X(nose.x),
          (midY + X(nose.y)) / 2 + v(0.08),
          0.2 * d,
          (X(nose.y) - midY) / 2 + v(0.1),
          1,
          1.1,
          -0.6,
        ],
      ],
      cheeks: eyes.map(function (e) {
        return [
          e.x + e.out * 0.2 * d,
          e.y + v(0.45),
          0.34 * d,
          v(0.34),
          e.out,
          0.85,
          0.7,
        ];
      }),
      under_eyes: eyes.map(function (e) {
        return [e.x, e.y + v(0.22), 0.28 * d, v(0.1), e.out, 1.3, 0];
      }),
      chin: [
        [X(mouth.x), X(mouth.y) + v(0.6), 0.48 * d, v(0.28), 1, 0.8, -0.7],
      ],
    };
  }

  // Her photo with the zones the scan spoke about: a dashed outline where it
  // looked, a numbered fill where it found something (2 and up). The photo is
  // this page's own copy and never leaves her phone.
  function faceMap(scan, hits) {
    var wrap = h("div", { class: "sa-map" }, [
      h("img", { src: state.photoUrl, alt: "" }),
    ]);
    var mesh =
      state.faceMesh && state.faceMesh.length >= 468 ? state.faceMesh : null;
    var kp = state.faceKp && state.faceKp.length >= 4 ? state.faceKp : null;
    if (!mesh && !kp) return wrap;
    var shapes = mesh ? meshShapes(mesh) : pointShapes(kp);
    var svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 100 100");
    svg.setAttribute("preserveAspectRatio", "none");
    svg.setAttribute("aria-hidden", "true");
    var seen = {};
    (scan.zone_notes || []).forEach(function (n) {
      if (shapes[n.zone])
        seen[n.zone] = Math.max(seen[n.zone] || 0, n.severity || 0);
    });
    Object.keys(seen).forEach(function (zone) {
      var num = hits.indexOf(zone) + 1;
      shapes[zone].forEach(function (s, i) {
        var el = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "ellipse",
        );
        el.setAttribute("cx", s[0]);
        el.setAttribute("cy", s[1]);
        el.setAttribute("rx", s[2]);
        el.setAttribute("ry", s[3]);
        if (num) el.setAttribute("class", "is-hit");
        svg.appendChild(el);
        if (num && i === 0)
          wrap.appendChild(
            h(
              "span",
              {
                class: "sa-map__n",
                style:
                  "left:" +
                  (s[0] + s[4] * s[2] * s[5]) +
                  "%;top:" +
                  (s[1] + s[3] * s[6]) +
                  "%",
              },
              String(num),
            ),
          );
      });
    });
    wrap.appendChild(svg);
    return wrap;
  }

  // The zones worth a number (2 and up), in the scan's own order.
  function zoneHits(scan) {
    var zones = (state.cfg && state.cfg.zoneLabels) || {};
    var hits = [];
    (scan.zone_notes || []).forEach(function (n) {
      if (n.severity >= 2 && zones[n.zone] && hits.indexOf(n.zone) < 0)
        hits.push(n.zone);
    });
    return hits;
  }

  function scanSummary(scan) {
    var cfg = state.cfg || {};
    var labels = cfg.scoreLabels || {};
    var zones = cfg.zoneLabels || {};
    var box = h("div", { class: "sa-scores" }, [h("p", null, t("photo_seen"))]);
    var scores = scan.scores || {};
    Object.keys(labels).forEach(function (k) {
      var v = scores[k];
      if (typeof v !== "number" || v < 1) return;
      var dots = [];
      for (var i = 1; i <= 4; i++)
        dots.push(h("i", { class: i <= v ? "on" : "" }));
      box.appendChild(
        h("div", { class: "sa-score" }, [
          h("span", { class: "sa-score__name" }, labels[k]),
          h("span", { class: "sa-score__bar", "aria-label": v + "/4" }, dots),
        ]),
      );
    });
    var hits = zoneHits(scan);
    var mapped = !!(state.photoUrl && (state.faceKp || state.faceMesh));
    if (state.photoUrl) box.insertBefore(faceMap(scan, hits), box.firstChild);
    var obs = [];
    if (scan.observations_he)
      obs.push(h("li", null, noDash(scan.observations_he)));
    (scan.zone_notes || []).forEach(function (n) {
      if (n.severity >= 2 && zones[n.zone])
        obs.push(
          h("li", null, [
            mapped
              ? h(
                  "span",
                  { class: "sa-obs__n" },
                  String(hits.indexOf(n.zone) + 1),
                )
              : null,
            zones[n.zone] + ": " + noDash(n.concern),
          ]),
        );
    });
    if (obs.length) box.appendChild(h("ul", { class: "sa-obs" }, obs));
    return box;
  }

  // ── the result ──
  function results() {
    state.step = "loading";
    var sk = h(
      "div",
      { class: "sa-skel", role: "status", "aria-label": str("res_title") },
      [
        h("i", { class: "sa-skel__title" }),
        h("i", { class: "sa-skel__pills" }),
        h("i", { class: "sa-skel__row" }),
        h("i", { class: "sa-skel__row" }),
        h("i", { class: "sa-skel__row" }),
        h("i", { class: "sa-skel__row" }),
      ],
    );
    show(sk);
    var a = state.a;
    api("/api/skin/routine", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // The concerns she confirmed already carry what the photo saw.
      body: JSON.stringify({ answers: a }),
    })
      .then(function (r) {
        state.result = r;
        state.step = "results";
        render();
      })
      .catch(function () {
        state.step = "size";
        render();
        stage
          .querySelector(".sa-step")
          .appendChild(h("p", { class: "sa-error", role: "alert" }, "⚠"));
      });
  }

  // "פיגמנטציה / כתמים / גוון לא אחיד" → "פיגמנטציה" on a small tag
  // A Latin product name inside Hebrew is isolated, so a number after it stays in
  // the Hebrew order ("Infinity Serum 2 עד 3 ערבים" otherwise shows the 2 first).
  var FSI = String.fromCharCode(0x2068),
    PDI = String.fromCharCode(0x2069);
  function iso(text) {
    return String(text || "").replace(
      /[A-Za-z][\w&%.'+]*(?: +&? *\d*[A-Za-z][\w&%.'+]*)*/g,
      function (m) {
        return FSI + m + PDI;
      },
    );
  }
  // A step note that repeats the product's own name says nothing new.
  function noteFor(s) {
    var note = s.note || "";
    var name = productName(s.product);
    if (name && note.indexOf(name) === 0)
      note = note.slice(name.length).replace(/^[\s,]+/, "");
    return note;
  }
  function svgIcon(kind) {
    var sv = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    sv.setAttribute("viewBox", "0 0 24 24");
    sv.setAttribute("aria-hidden", "true");
    sv.setAttribute("class", "sa-ico");
    sv.innerHTML =
      kind === "camera"
        ? '<path fill="currentColor" d="M9 4 7.2 6H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-3.2L15 4H9Zm3 4.5a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Zm0 2a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Z"/>'
        : '<path fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" d="M5 12.5l4.2 4.2L19 7"/>';
    return sv;
  }
  function productName(p) {
    if (p.hebrewName && /[֐-׿]/.test(p.hebrewName)) {
      // a Shopify title like "קרם הגנה מבוסס מינרלי- Breezy Whisper ..." → its Hebrew part
      var m = p.hebrewName.match(/^([֐-׿][^A-Za-z]*?)\s*-\s*[A-Za-z]/);
      return m ? m[1].trim() : p.hebrewName;
    }
    return p.hebrewName || p.title || p.name;
  }
  function addToCart(ids, btn) {
    if (!ids.length || btn.disabled) return;
    btn.disabled = true;
    btn.classList.add("is-busy");
    // The site's cart drawer watches /cart/add.js and opens by itself (as after
    // any "הוספה לסל"); never send her to the /cart page (owner, 28.8.2026).
    fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      // `_asif_skin` marks the line as added here (owner 2.11.2026: "does the scan
      // sell?"). A leading "_" keeps it out of the cart and the checkout; the hub's
      // order index keeps it as `from: "skin"` (src/lib/orders/syncOrder.ts).
      body: JSON.stringify({
        items: ids.map(function (id) {
          return {
            id: Number(id),
            quantity: 1,
            properties: { _asif_skin: "1" },
          };
        }),
      }),
    })
      .catch(function () {})
      .then(function () {
        btn.disabled = false;
        btn.classList.remove("is-busy");
      });
  }

  // 28.10.2026 preview: the routine as a personal bundle, priced by the hub's live tiers (program "bundles").
  // The hub builds the bundle on the builder page; here only the live percent and the link with the routine's ids.
  function bundleBox(ids, rt) {
    var box = h("div", { class: "sa-bundle", hidden: true });
    // Preview theme only until the owner approves its sentences (copy.json "לאישור הבעלים").
    if (!PREVIEW || !HUB || !ids || ids.length < 2) return box;
    fetch(HUB + "/api/bundles/custom?program=bundles", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cfg) {
        if (!cfg || !cfg.ok || !cfg.tiers) return;
        var n = Math.min(ids.length, cfg.max || 8);
        if (n < (cfg.min || 2)) return;
        var rate = 0, best = -1;
        Object.keys(cfg.tiers).forEach(function (k) { var cnt = Number(k); if (n >= cnt && cnt > best) { best = cnt; rate = cfg.tiers[k]; } });
        if (!rate) return;
        var total = Math.round(rt.total * (100 - rate)) / 100;
        var url = "/collections/" + encodeURIComponent("מארזים") + "?view=bundles-builder&add=" + ids.slice(0, n).join(",");
        box.appendChild(h("p", { class: "sa-bundle__kicker" }, t("res_bundle_kicker")));
        box.appendChild(h("p", { class: "sa-bundle__line", dir: "rtl" }, t("res_bundle_line", { count: n, percent: rate, total: "⁦" + money(total) + "⁩", full: "⁦" + money(rt.total) + "⁩" })));
        box.appendChild(h("a", { class: "sa-btn sa-bundle__btn", href: url }, t("res_bundle_button")));
        box.removeAttribute("hidden");
      })
      .catch(function () {});
    return box;
  }

  function routineList(r) {
    var steps = state.time === "am" ? r.am : r.pm;
    var ol = h("ol", { class: "sa-steps is-enter" });
    steps.forEach(function (s, i) {
      var add = h(
        "button",
        {
          type: "button",
          class: "sa-btn sa-btn--ghost sa-add sa-add--row",
          onclick: function () {
            addToCart([s.product.variantId], add);
          },
        },
        t("res_add"),
      );
      ol.appendChild(
        h("li", { class: "sa-stepi" }, [
          h("span", { class: "sa-stepi__n" }, String(i + 1)),
          h(
            "span",
            { class: "sa-stepi__media" },
            h("img", {
              class: "sa-stepi__img",
              src: s.product.imageUrl,
              alt: "",
              loading: "lazy",
              width: 72,
              height: 72,
            }),
          ),
          h("div", null, [
            h("p", { class: "sa-stepi__name" }, productName(s.product)),
            // Why it was picked: the first words of her concern lines, else her skin type
            // (res_why, approved by the owner 2.11.2026).
            s.why && s.why.length
              ? h(
                  "p",
                  { class: "sa-stepi__why" },
                  t("res_why", {
                    reasons: iso(
                      s.why
                        .map(function (w) {
                          return w.split(" / ")[0];
                        })
                        .join(" · "),
                    ),
                  }),
                )
              : null,
            noteFor(s)
              ? h("p", { class: "sa-stepi__note" }, iso(noteFor(s)))
              : null,
          ]),
          add,
        ]),
      );
    });
    return ol;
  }

  function resultsView() {
    var r = state.result;
    var wrap = h("div");
    var pills = h("div", { class: "sa-pills" }, [
      h("span", { class: "sa-pill" }, r.family.label),
    ]);
    r.concerns.forEach(function (c) {
      pills.appendChild(h("span", { class: "sa-pill sa-pill--line" }, c.label));
    });
    wrap.appendChild(
      h("div", { class: "sa-res__head" }, [
        h("h2", null, t("res_title")),
        pills,
      ]),
    );
    if (r.referral)
      wrap.appendChild(
        h("p", { class: "sa-referral", role: "note" }, t("res_referral")),
      );

    var grid = h("div", { class: "sa-res__grid" });
    if (r.routine) {
      var rt = r.routine;
      var holder = h("div");
      var seg = h("div", { class: "sa-seg", role: "group" });
      [
        ["am", "res_am"],
        ["pm", "res_pm"],
      ].forEach(function (x) {
        seg.appendChild(
          h(
            "button",
            {
              type: "button",
              "aria-pressed": state.time === x[0] ? "true" : "false",
              onclick: function () {
                state.time = x[0];
                seg.querySelectorAll("button").forEach(function (b, i) {
                  b.setAttribute(
                    "aria-pressed",
                    (i === 0) === (state.time === "am") ? "true" : "false",
                  );
                });
                holder.innerHTML = "";
                holder.appendChild(routineList(rt));
              },
            },
            t(x[1]),
          ),
        );
      });
      holder.appendChild(routineList(rt));
      var extra = h("div", { class: "sa-extra" });
      rt.booster.forEach(function (b) {
        extra.appendChild(
          h("p", null, [h("b", null, t("res_booster")), " " + iso(b)]),
        );
      });
      rt.tips.forEach(function (tip) {
        extra.appendChild(
          h("p", null, [h("b", null, t("res_tip")), " " + iso(tip)]),
        );
      });
      var ids = [];
      rt.am.concat(rt.pm).forEach(function (s) {
        if (ids.indexOf(s.product.variantId) < 0) ids.push(s.product.variantId);
      });
      var addAll = h(
        "button",
        {
          type: "button",
          class: "sa-btn sa-btn--ghost",
          onclick: function () {
            addToCart(ids, addAll);
          },
        },
        t("res_add_all"),
      );
      grid.appendChild(
        h("section", null, [
          h("h3", null, rt.title),
          seg,
          holder,
          extra,
          bundleBox(ids, rt),
          h("div", { class: "sa-sum" }, [
            h("div", { class: "sa-sum__txt" }, [
              t(rt.kind === "minimal" ? "res_sum_minimal" : "res_sum", {
                n: rt.count,
              }),
              h("span", { class: "sa-sum__total" }, money(rt.total)),
            ]),
            addAll,
          ]),
        ]),
      );
    }
    wrap.appendChild(grid);
    wrap.appendChild(
      h("div", { class: "sa-foot" }, [
        h("p", null, t("res_lilach")),
        h(
          "button",
          {
            type: "button",
            class: "sa-link",
            onclick: function () {
              state.a = { concerns: [] };
              if (state.photoUrl) URL.revokeObjectURL(state.photoUrl);
              state.photoUrl = null;
              state.faceKp = null;
              state.faceMesh = null;
              state.scan = null;
              state.fromScan = [];
              state.result = null;
              go("intro");
            },
          },
          t("res_restart"),
        ),
      ]),
    );
    // Owner-only, on the preview theme: which rule produced what.
    if (PREVIEW)
      wrap.appendChild(
        h("details", { class: "sa-trace" }, [
          h(
            "summary",
            null,
            "איך זה נקבע (רק בתצוגה המקדימה, לקוחות לא רואות)",
          ),
          h(
            "ol",
            null,
            r.trace.map(function (x) {
              return h("li", null, x);
            }),
          ),
        ]),
      );
    return wrap;
  }

  function intro() {
    var icons = h("div", { class: "sa-intro__icons", "aria-hidden": "true" });
    ["dry", "combo", "oily", "normal"].forEach(function (k) {
      if (ICONS[k])
        icons.appendChild(
          h("img", { src: ICONS[k], alt: "", width: 56, height: 56 }),
        );
    });
    return h("div", { class: "sa-intro" }, [
      icons,
      h("h2", null, t("intro_title")),
      h("p", { class: "sa-lead" }, t("intro_text")),
      h(
        "button",
        {
          type: "button",
          class: "sa-btn",
          onclick: function () {
            go(FLOW[0]);
          },
        },
        t("start"),
      ),
    ]);
  }

  function render() {
    var s = state.step;
    if (s === "intro") return show(intro());
    if (s === "photo") return show(photo());
    if (s === "concerns") return show(concerns());
    if (s === "results") return show(resultsView());
    if (Q[s]) return show(single(s));
  }

  render();
})();
